import { useEffect, useState } from 'react'
import Modal from '../../Modal'
import ArtisanService from '../../../api/artisan-services'
import { showToast } from '../../../utils/toast'
import { handleErrorResponse } from '../../../utils/handleErrorResponse'
import FormInput from '../../Input'

type Props = {
  open: boolean
  estateId: string
  initialService?: string
  initialDescription?: string
  onClose: () => void
  onAdd: () => void
}

export default function AddServiceModal({
  open,
  estateId,
  initialService = '',
  initialDescription = '',
  onClose,
  onAdd,
}: Props) {
  const [serviceType, setServiceType] = useState(initialService)
  const [description, setDescription] = useState(initialDescription)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open) {
      setServiceType(initialService)
      setDescription(initialDescription)
    }
  }, [open, initialService, initialDescription])

  const isValid = serviceType.trim().length > 0 && estateId.trim().length > 0

  const handleAdd = async () => {
    if (!isValid) return
    setSubmitting(true)

    const payload = {
      name: serviceType.trim(),
      description: description.trim(),
      estateId: estateId,
    }

    try {
      const response = await ArtisanService.createService(payload)

      showToast({
        title: 'Success',
        description: `Service '${serviceType.trim()}' created successfully.`,
        status: 'success',
        id: 'add-service-success',
      })

      setServiceType('')
      setDescription('')
      onAdd()
      onClose()
    } catch (error) {
      handleErrorResponse(error, 'add-service-error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      close={onClose}
      showClose={false}
      widthClass="w-[calc(100%-32px)] sm:max-w-lg md:max-w-2xl"
    >
      <div className="w-full overflow-y-auto max-h-[90vh]">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-base sm:text-lg font-semibold">Add estate service</h3>
          <button
            aria-label="close"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 flex-shrink-0 ml-2"
          >
            ✕
          </button>
        </div>

        <div className="space-y-3 sm:space-y-4 px-2">
          <div>
            <FormInput
              name="serviceType"
              label="Service type"
              placeholder="e.g Plumbing"
              value={serviceType}
              onChange={(e) => setServiceType(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm text-gray-600 mb-2">
              Description
            </label>
            <textarea
              className="w-full border rounded px-3 py-2 text-sm h-24 resize-none focus:outline-black"
              placeholder=""
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded text-sm bg-white"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleAdd}
            disabled={!isValid || submitting}
            className={`px-4 py-2 rounded text-sm text-white ${isValid && !submitting ? 'bg-black' : 'bg-gray-300 cursor-not-allowed'}`}
          >
            {submitting ? 'Adding...' : 'Add'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
